/**
 * Template base data + schemas (hand-authored single source of truth).
 *
 * Holds the static, human-edited fields for every template plus the shared
 * schemas. The per-template `theme` summary is NOT here — it is generated from
 * each template's `theme.css` + `font-manifest.json` by
 * `scripts/extract-theme.ts` into `themes.generated.ts`, then composed onto
 * each entry in `registry.ts`.
 *
 * This module imports nothing generated, so the extractor can import it without
 * a circular/bootstrap dependency on its own output.
 *
 * @module
 */
import { Schema as S } from "effect"

export const TemplateIdSchema = S.Literals(["tpl_one", "tpl_two", "tpl_three"])
export type TemplateId = typeof TemplateIdSchema.Type

/**
 * Compact, non-technical theme identity used by the picker UIs. Generated from
 * the template's source theme files — never hand-edited.
 */
export const ThemeSummarySchema = S.Struct({
  colors: S.Struct({
    /** primary / brand accent (`--color-brand`) */
    brand: S.String,
    /** text/icon color on top of brand (`--color-brand-fg`) */
    brandFg: S.String,
    /** page background (`--color-bg`) */
    bg: S.String,
    /** cards / panels (`--color-surface`) */
    surface: S.String,
    /** primary text (`--color-text`) */
    text: S.String,
    /** secondary / muted text (`--color-text-soft`) */
    textSoft: S.String,
    /** dividers / outlines (`--color-border`) */
    border: S.String,
  }),
  fonts: S.Struct({
    /** Google font for headings (font-manifest `heading.name`) */
    heading: S.String,
    /** Google font for body copy (font-manifest `body.name`) */
    body: S.String,
  }),
  /** card/button corner rounding (`--radius-md`, e.g. "0.75rem") */
  radiusMd: S.String,
})
export type ThemeSummary = typeof ThemeSummarySchema.Type

/** A template's hand-authored fields (everything except the generated `theme`). */
export const TemplateBaseSchema = S.Struct({
  /** Stored as `site_versions.sourceVersionId`; must start with `tpl_`. */
  id: TemplateIdSchema,
  /** Directory name under heysegment-templates/ and preview subdomain label. */
  slug: S.String,
  /** Picker card title. */
  name: S.String,
  /** One short, non-technical line for the picker card. */
  tagline: S.String,
  /** Longer one-liner. */
  description: S.String,
  /** Live preview deployment. */
  previewUrl: S.String,
  /** R2 key of the seed bundle the VTM pulls when creating from this template. */
  bundleKey: S.String,
})
export type TemplateBase = typeof TemplateBaseSchema.Type

export const TEMPLATE_BASE: ReadonlyArray<TemplateBase> = [
  {
    id: "tpl_one",
    slug: "template-one",
    name: "Fresh Teal",
    tagline: "Clean & modern",
    description: "Clean, modern layout with a fresh teal palette.",
    previewUrl: "https://template-one.heysegment.com",
    bundleKey: "templates/template-one/current.tar.gz",
  },
  {
    id: "tpl_two",
    slug: "template-two",
    name: "Gold & Navy",
    tagline: "Premium & refined",
    description: "Premium feel — warm antique gold accents on deep navy.",
    previewUrl: "https://template-two.heysegment.com",
    bundleKey: "templates/template-two/current.tar.gz",
  },
  {
    id: "tpl_three",
    slug: "template-three",
    name: "Warm Orange",
    tagline: "Bold & friendly",
    description: "Bold, friendly design with warm orange accents.",
    previewUrl: "https://template-three.heysegment.com",
    bundleKey: "templates/template-three/current.tar.gz",
  },
]
